import { Box, Card, CardContent, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import RouteIcon from '@mui/icons-material/Route';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { api } from '@/api/client';
import { formatDate } from '@/utils/format';

type Enrollment = {
  id: number;
  template: { code: string; name: string };
  patient: number;
  patient_initials: string;
  status: 'active' | 'paused' | 'completed' | 'withdrawn';
  started_at: string;
  expected_end_at: string | null;
  steps: { id: number; status: string }[];
};

export function ClinicianPathwaysPage() {
  const q = useApiQuery({
    queryKey: ['clinician', 'pathways'],
    queryFn:  () => api.get<Enrollment[]>('/api/medical/clinician/pathway-enrollments/'),
  });

  return (
    <>
      <PageHeader
        title="Care pathways"
        subtitle="Patients you're leading through long-term care plans."
      />
      <DataState query={q} empty={{ title: 'You\'re not leading any pathway enrollments yet' }}>
        {(items) => (
          <Stack spacing={2}>
            {items.map((e) => <PathwayCard key={e.id} enrollment={e} />)}
          </Stack>
        )}
      </DataState>
    </>
  );
}

function PathwayCard({ enrollment }: { enrollment: Enrollment }) {
  const total = enrollment.steps?.length ?? 0;
  const done = (enrollment.steps ?? []).filter((s) => s.status === 'completed').length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <RouteIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {enrollment.template?.name ?? enrollment.template?.code}
              </Typography>
              <Chip size="small" variant="outlined" label={`Patient ${enrollment.patient_initials}`} />
              <Chip size="small" label={enrollment.status}
                    color={enrollment.status === 'active' ? 'primary'
                           : enrollment.status === 'completed' ? 'success'
                           : 'default'}
                    variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary" className="tabular">
              started {formatDate(enrollment.started_at)}
              {enrollment.expected_end_at && ` · expected ${formatDate(enrollment.expected_end_at)}`}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Progress</Typography>
                <Typography variant="caption" className="tabular" fontWeight={600}>
                  {done} / {total} ({pct}%)
                </Typography>
              </Stack>
              <LinearProgress variant="determinate" value={pct}
                              sx={{ height: 6, borderRadius: 3 }} />
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
